import { useCallback, useEffect, useRef, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { useHistory, useLocation } from "react-router-dom";
import "./landing.css";

/* Section ids are a contract with the landing sections — changing one here
   without changing the matching `id` on the section breaks that anchor. */
const PAGES = [
  { title: "Home", id: "home", kind: "anchor" },
  { title: "About", id: "aboutus", kind: "anchor" },
  { title: "Quality", id: "qualityassured", kind: "anchor" },
  { title: "Ordering", id: "ordering", kind: "anchor" },
  { title: "Delivery", id: "delivery", kind: "anchor" },
  { title: "Contact", id: "contactus", kind: "anchor" },
  { title: "Privacy", id: "privacypolicy", kind: "route", path: "/privacypolicy" },
  { title: "Login", id: "login", kind: "route", path: "/login" },
];

const ANCHOR_IDS = PAGES.filter((p) => p.kind === "anchor").map((p) => p.id);

function scrollToSection(id) {
  if (id === "home") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }
  const section = document.getElementById(id);
  if (!section) return false;
  // Offset is handled by `scroll-margin-top` in landing.css, so the header
  // height lives in one place instead of being hardcoded here.
  section.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

function Navbar() {
  const history = useHistory();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("home");
  const pendingScroll = useRef(null);

  const onLanding = location.pathname === "/" || location.pathname === "/home";

  /* Route-based pages own the active state; on the landing page the observer
     below takes over. */
  useEffect(() => {
    if (location.pathname.startsWith("/privacypolicy")) setActive("privacypolicy");
    else if (location.pathname.startsWith("/login")) setActive("login");
    else setActive("home");
  }, [location.pathname]);

  /* Deep link: /home#delivery, and hand-offs from the privacy/login pages. */
  useEffect(() => {
    if (!onLanding) return;
    const target = pendingScroll.current || location.hash.replace("#", "");
    pendingScroll.current = null;
    if (!target) return;
    // Effects run after the whole tree has committed, so the section is
    // normally already there. Retry once for the route-change case rather than
    // waiting on a frame, which never arrives in a background tab.
    if (scrollToSection(target)) return;
    const retry = setTimeout(() => scrollToSection(target), 80);
    return () => clearTimeout(retry);
  }, [onLanding, location.hash, location.key]);

  /* Scroll spy — the underline follows the section you are actually reading. */
  useEffect(() => {
    if (!onLanding || typeof IntersectionObserver === "undefined") return;
    const sections = ANCHOR_IDS.map((id) => document.getElementById(id)).filter(
      Boolean
    );
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [onLanding]);

  /* Lock the page behind the mobile drawer, and let Escape close it. */
  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const go = useCallback(
    (e, page) => {
      e.preventDefault();
      setMenuOpen(false);

      if (page.kind === "route") {
        history.push(page.path);
        window.scrollTo({ top: 0 });
        return;
      }

      if (!onLanding) {
        // Coming from /login or /privacypolicy: land first, then scroll.
        pendingScroll.current = page.id;
        history.push(page.id === "home" ? "/home" : `/home#${page.id}`);
        return;
      }

      setActive(page.id);
      scrollToSection(page.id);
      if (page.id !== "home") history.replace(`/home#${page.id}`);
    },
    [history, onLanding]
  );

  return (
    <header className="tfm-header">
      <div className="tfm-container tfm-header__inner">
        <a
          className="tfm-header__logo"
          href="/home"
          onClick={(e) => go(e, PAGES[0])}
          aria-label="Tripoli Fresh Market Wholesale — home"
        >
          <img src="/images/fulllogo.png" alt="Tripoli Fresh Market Wholesale" />
        </a>

        <button
          type="button"
          className="tfm-header__toggle"
          aria-expanded={menuOpen}
          aria-label="Open menu"
          onClick={() => setMenuOpen(true)}
        >
          <FaBars />
        </button>

        <nav
          className={`tfm-nav${menuOpen ? " is-open" : ""}`}
          aria-label="Main"
        >
          <button
            type="button"
            className="tfm-drawer__close"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <FaTimes />
          </button>

          {PAGES.map((page) => (
            <a
              key={page.id}
              className="tfm-nav__link"
              href={page.kind === "route" ? page.path : `/home#${page.id}`}
              aria-current={active === page.id ? "true" : undefined}
              onClick={(e) => go(e, page)}
            >
              {page.title}
            </a>
          ))}

          <a
            className="tfm-btn tfm-btn--green tfm-nav__cta"
            href="/home#ordering"
            onClick={(e) => go(e, PAGES[3])}
          >
            Order now
          </a>
        </nav>

        <a
          className="tfm-btn tfm-btn--green tfm-header__cta"
          href="/home#ordering"
          onClick={(e) => go(e, PAGES[3])}
        >
          Order now
        </a>
      </div>
    </header>
  );
}

export default Navbar;
